import { httpsCallable } from 'firebase/functions';
import { functions } from '@/config/firebase';
import { employeeNamesService } from './employeeNames';

interface EmailNotificationData {
  type: 'task_assigned' | 'task_updated' | 'task_completed' | 'comment_added' | 'mention' | 'task_status_changed';
  recipientEmail: string;
  taskId: string;
  taskTitle: string;
  projectName: string;
  assignerName: string;
  assignerEmail: string;
  newStatus?: string;
  commentContent?: string;
}

export const productManagerEmailService = {
  /**
   * Send an email notification for task assignment
   */
  async sendTaskAssignedNotification(
    recipientEmail: string,
    taskId: string,
    taskTitle: string,
    projectName: string,
    assignerEmail: string
  ): Promise<void> {
    try {
      // Get the assigner's display name
      const assignerName = await employeeNamesService.getNameFromEmail(assignerEmail) || assignerEmail;

      const notificationData: EmailNotificationData = {
        type: 'task_assigned',
        recipientEmail,
        taskId,
        taskTitle,
        projectName,
        assignerName,
        assignerEmail
      };

      const sendNotification = httpsCallable(functions, 'sendProductManagerEmailNotification');
      const result = await sendNotification(notificationData);
      
    } catch (error) {
      // Don't throw error to prevent blocking task creation
    }
  },

  /**
   * Send an email notification for task updates
   */
  async sendTaskUpdatedNotification(
    recipientEmail: string,
    taskId: string,
    taskTitle: string,
    projectName: string,
    updaterEmail: string
  ): Promise<void> {
    try {
      const updaterName = await employeeNamesService.getNameFromEmail(updaterEmail) || updaterEmail;

      const notificationData: EmailNotificationData = {
        type: 'task_updated',
        recipientEmail,
        taskId,
        taskTitle,
        projectName,
        assignerName: updaterName,
        assignerEmail: updaterEmail
      };

      const sendNotification = httpsCallable(functions, 'sendProductManagerEmailNotification');
      const result = await sendNotification(notificationData);
      
    } catch (error) {
    }
  },

  /**
   * Send an email notification for task completion
   */
  async sendTaskCompletedNotification(
    recipientEmail: string,
    taskId: string,
    taskTitle: string,
    projectName: string,
    completerEmail: string
  ): Promise<void> {
    try {
      const completerName = await employeeNamesService.getNameFromEmail(completerEmail) || completerEmail;

      const notificationData: EmailNotificationData = {
        type: 'task_completed',
        recipientEmail,
        taskId,
        taskTitle,
        projectName,
        assignerName: completerName,
        assignerEmail: completerEmail
      };

      const sendNotification = httpsCallable(functions, 'sendProductManagerEmailNotification');
      const result = await sendNotification(notificationData);
      
    } catch (error) {
    }
  },

  /**
   * Send an email notification for new comments
   */
  async sendCommentAddedNotification(
    recipientEmail: string,
    taskId: string,
    taskTitle: string,
    projectName: string,
    commenterEmail: string
  ): Promise<void> {
    try {
      const commenterName = await employeeNamesService.getNameFromEmail(commenterEmail) || commenterEmail;

      const notificationData: EmailNotificationData = {
        type: 'comment_added',
        recipientEmail,
        taskId,
        taskTitle,
        projectName,
        assignerName: commenterName,
        assignerEmail: commenterEmail
      };

      const sendNotification = httpsCallable(functions, 'sendProductManagerEmailNotification');
      const result = await sendNotification(notificationData);
      
    } catch (error) {
    }
  },

  /**
   * Send an email notification for mentions
   */
  async sendMentionNotification(
    recipientEmail: string,
    taskId: string,
    taskTitle: string,
    projectName: string,
    mentionerEmail: string,
    commentContent: string
  ): Promise<void> {
    try {
      const mentionerName = await employeeNamesService.getNameFromEmail(mentionerEmail) || mentionerEmail;

      const notificationData: EmailNotificationData = {
        type: 'mention',
        recipientEmail,
        taskId,
        taskTitle,
        projectName,
        assignerName: mentionerName,
        assignerEmail: mentionerEmail,
        commentContent
      };

      const sendNotification = httpsCallable(functions, 'sendProductManagerEmailNotification');
      const result = await sendNotification(notificationData);
      
    } catch (error) {
    }
  },

  /**
   * Send an email notification for task status changes
   */
  async sendTaskStatusChangedNotification(
    recipientEmail: string,
    taskId: string,
    taskTitle: string,
    projectName: string,
    changerEmail: string,
    newStatus: string
  ): Promise<void> {
    try {
      const changerName = await employeeNamesService.getNameFromEmail(changerEmail) || changerEmail;

      const notificationData: EmailNotificationData = {
        type: 'task_status_changed',
        recipientEmail,
        taskId,
        taskTitle,
        projectName,
        assignerName: changerName,
        assignerEmail: changerEmail,
        newStatus
      };

      const sendNotification = httpsCallable(functions, 'sendProductManagerEmailNotification');
      const result = await sendNotification(notificationData);
      
    } catch (error) {
    }
  }
};